// Discovery and fast travel in the sim (gate D2.2), as the "discovery" kernel module on the streamed valley:
//   valley:physics step (order 100)
//   places:interiors   (order 120)
//   discovery:step     (order 130)  every `every` steps: the cells round the Wanderer revealed, the points of interest
//                                   in range checked (seen: a line of sight from the eye to the place, a ray through the
//                                   terrain and the solids; reached: in reach), `world:discovered` for each new mark.
// Fast travel is the `discovery:travel` action (a menu's choice, applied at once as sim state, like the sky's
// actions): it checks the place is a reached waypoint and that the Wanderer is outdoors and out of a fight, then puts
// the Wanderer there through the world's teleport (its chunks loaded at once). The state is a kernel component, so it
// is in the hash and in the save (its own slice, version 1); a save from before the module carries no slice and starts
// with nothing discovered.
import { Discovery, type DiscoveryState, type PoiMark, type PointOfInterest } from "@rpg/world";
import type { GameModule, World } from "@rpg/kernel";
import type { ValleyRig } from "../valley/scene";
import type { GameEvents } from "../core";
import type { MapContent } from "./content";

export const DISCOVERY_C = "discovery/State";

/** Why a fast travel was refused (the discovery's reasons, and the game's). */
export type TravelRefusalReason = "unknown" | "not-a-waypoint" | "not-reached" | "same-place" | "fight" | "indoors";

/** The module's state (a kernel component). */
export interface DiscoverySimState {
  discovery: DiscoveryState;
  /** Steps since the module began. */
  step: number;
  /** The last travel asked for and how it went. */
  lastTravel: { to: string; ok: boolean; reason: TravelRefusalReason | null; step: number } | null;
  travels: number;
}

/** A fresh state (a new game, or a save from before the module joined). */
export const freshDiscoveryState = (): DiscoverySimState => ({ discovery: Discovery.empty(), step: 0, lastTravel: null, travels: 0 });

/** The actions it registers. */
export const DISCOVERY_ACTIONS = { travel: "discovery:travel", mark: "discovery:mark", reveal: "discovery:reveal" } as const;

/** What the HUD and the handle read. */
export interface DiscoverySim {
  /** The discovery (its state mirrors the component after each step). */
  discovery: Discovery;
  state(): DiscoverySimState;
  /** The last 200 marks, oldest first (the step each was made on). */
  log: { step: number; poi: string; mark: PoiMark }[];
  /** The waypoint the Wanderer stands at, if any (within its reach). */
  at(): string | null;
}

/** Build the module. `teleport` puts the Wanderer down at (x, z) facing `yaw`, loading what it stands on. */
export function createDiscoveryModule(o: {
  rig: () => ValleyRig;
  map: MapContent;
  heightAt: (x: number, z: number) => number;
  /** In a fight (an enemy alert to the Wanderer): no travel. */
  engaged: () => boolean;
  /** Inside a room: no travel. */
  indoors: () => boolean;
  teleport: (x: number, z: number, yaw?: number) => void;
}): { module: GameModule<GameEvents>; hashed: string[]; build(w: World): void; sim: DiscoverySim } {
  const disc = new Discovery(o.map.discovery);
  const every = o.map.row.every;
  const eye = o.map.row.eye;
  let world: World | null = null;
  const state = (): DiscoverySimState => {
    const e = world!.query(DISCOVERY_C)[0];
    return world!.getComponent<DiscoverySimState>(e!, DISCOVERY_C)!;
  };
  const log: DiscoverySim["log"] = [];
  // the discovery mirrors the component: put the component's state back into it only when it was replaced (a restore,
  // a fresh build), so the views' fog repaints only on real changes
  let mirrored: DiscoveryState | null = null;
  const sync = (s: DiscoverySimState): void => {
    if (s.discovery === mirrored) return;
    disc.restore(s.discovery);
    mirrored = s.discovery;
  };
  const commit = (s: DiscoverySimState): void => {
    s.discovery = disc.state();
    mirrored = s.discovery;
  };
  /** A line of sight from the Wanderer's eye to a point of interest (2 m over its ground). */
  const sees = (p: PointOfInterest): boolean => {
    const r = o.rig();
    const h = r.hero();
    const from = { x: h.feet.x, y: h.feet.y + eye, z: h.feet.z };
    const to = { x: p.x, y: o.heightAt(p.x, p.z) + 2, z: p.z };
    const d = { x: to.x - from.x, y: to.y - from.y, z: to.z - from.z };
    const len = Math.hypot(d.x, d.y, d.z);
    if (len < 1e-3) return true;
    const hit = r.collision.castRay(from, d, len, { layers: ["terrain", "solid"] });
    // something in the way, short of the place itself (a building's own walls count as the place)
    return !hit || hit.distance >= len - 6;
  };
  const at = (): string | null => {
    const h = o.rig().hero();
    for (const p of disc.pois) if (p.waypoint && Math.hypot(p.x - h.feet.x, p.z - h.feet.z) <= p.reachRange) return p.id;
    return null;
  };
  const module: GameModule<GameEvents> = {
    id: "discovery",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["world:discovered"] },
    dependencies: ["valley"],
    setup(ctx) {
      ctx.registerSystem(
        ({ events }) => {
          const s = state();
          s.step++;
          if ((s.step - 1) % every !== 0) return;
          sync(s);
          const v = disc.version;
          const h = o.rig().hero();
          disc.revealAround(h.feet.x, h.feet.z);
          for (const e of disc.check(h.feet.x, h.feet.z, sees)) {
            log.push({ step: s.step, poi: e.poi, mark: e.mark });
            if (log.length > 200) log.shift();
            events.emit("world:discovered", { poi: e.poi, mark: e.mark });
          }
          if (disc.version !== v) commit(s);
        },
        { name: "discovery:step", order: 130 },
      );
      ctx.registerAction(DISCOVERY_ACTIONS.travel, (payload) => {
        const s = state();
        const to = String((payload as { to?: unknown } | null)?.to ?? "");
        sync(s);
        const reason: TravelRefusalReason | null = disc.travelRefusal(to, at()) ?? (o.engaged() ? "fight" : o.indoors() ? "indoors" : null);
        s.lastTravel = { to, ok: reason === null, reason, step: s.step };
        if (reason) return;
        const p = disc.poi(to)!;
        const h = o.rig().hero();
        // arrive facing the way the Wanderer faced
        o.teleport(p.x, p.z, h.yaw);
        s.travels++;
      });
      // a quest (story) or a tool marks a place found without going there
      ctx.registerAction(DISCOVERY_ACTIONS.mark, (payload) => {
        const s = state();
        const q = payload as { poi?: string; mark?: PoiMark } | null;
        if (!q?.poi) return;
        sync(s);
        if (disc.mark(q.poi, q.mark === "reached" ? "reached" : "seen")) {
          log.push({ step: s.step, poi: q.poi, mark: q.mark === "reached" ? "reached" : "seen" });
          ctx.events.emit("world:discovered", { poi: q.poi, mark: q.mark === "reached" ? "reached" : "seen" });
        }
        commit(s);
      });
      // a bought map or a lookout reveals an area without walking it (and the specs reveal half the valley for a shot)
      ctx.registerAction(DISCOVERY_ACTIONS.reveal, (payload) => {
        const q = payload as { x?: number; z?: number; radius?: number } | null;
        if (typeof q?.x !== "number" || typeof q.z !== "number" || typeof q.radius !== "number") return;
        const s = state();
        sync(s);
        if (disc.revealAround(q.x, q.z, q.radius)) commit(s);
      });
      ctx.registerSaveSlice({
        version: 1,
        serialize: () => {
          const s = state();
          // the discovery's own state() drops the cells under its planted defect
          sync(s);
          return structuredClone({ ...s, discovery: disc.state() });
        },
        deserialize: (data) => {
          const d = data as Partial<DiscoverySimState> | null;
          Object.assign(state(), d && d.discovery ? structuredClone(d) : freshDiscoveryState());
          sync(state());
        },
      });
    },
  };
  return {
    module,
    hashed: [DISCOVERY_C],
    build(w) {
      world = w;
      w.addComponent<DiscoverySimState>(w.createEntity(), DISCOVERY_C, freshDiscoveryState());
    },
    sim: { discovery: disc, state: () => state(), log, at },
  };
}
